/**
 * RendPleX AI Advisor — property-scoped chat container.
 *
 * Owns:
 *   - Panel open/close state
 *   - Conversation state (messages, streaming, error)
 *   - Streaming fetch to /api/property/chat (SSE)
 *   - History load / clear via /api/property/chat/history
 *   - Analytics events via /api/property/chat/analytics
 *
 * All UI rendering is delegated to the reusable pieces in ./chat/*.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Sparkles, ArrowUpRight, Building2, HeartPulse, Zap, Target } from 'lucide-react';

import {
  ChatFloatingButton,
  ChatInput,
  ChatMessage,
  ChatPanel,
  FitChip,
  SuggestedPrompts,
  TypingIndicator,
} from './chat';
import { detectPortfolioIntent } from './chat/portfolioIntent';
import { useAdvisorContext } from '../context/PropertyAdvisorContext';
import { fireChatAnalytics } from '../lib/chatAnalytics';
import {
  clearPendingQuestion,
  readPendingQuestion,
} from '../lib/pendingAdvisorQuestion';
import {
  hasSeenAdvisorTour,
  markAdvisorTourSeen,
} from '../lib/advisorTour';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cross-Page Conversation: every Portfolio-Aware Advisor mount shares a
// single per-user thread on the backend so a Premium user's conversation
// carries seamlessly between /portfolio → /portfolio-doctor →
// /stress-test → /rent-optimizer. Deal-Advisor mounts keep their own
// per-property key (derived from address/purchase_price).
const PORTFOLIO_ADVISOR_KEY = 'portfolio-advisor';

// Thread Clear Per Session: after this many messages in a Portfolio-Aware
// thread we surface a "Start a new conversation" pill in the header so
// users can begin a clean context without hunting for the tiny clear
// icon.
const LONG_THREAD_THRESHOLD = 10;

const fireAnalytics = fireChatAnalytics;

export default function AIPropertyChat({ propertyContext: propOverride }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const contextFromProvider = useAdvisorContext();
  // Explicit prop always wins; otherwise fall back to the nearest provider.
  const propertyContext = propOverride ?? contextFromProvider ?? {};
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [propertyKey, setPropertyKey] = useState(null);
  // { plan, capabilities, monthly_message_quota, used_this_month, remaining,
  //   unlimited, advisor_access, portfolio_context, billing_period_start }
  const [usage, setUsage] = useState(null);
  const proWarningFiredRef = useRef(false);
  const proLimitFiredRef = useRef(false);
  // One-tap retry: after Premium upgrade, auto-restore the user's
  // original portfolio question and send it as their first Premium query.
  const autoRetriedRef = useRef(false);
  // Cross-Page Conversation: track the label of the last context we
  // showed so we can insert a subtle "Context changed" system row when
  // the user navigates between /portfolio → /portfolio-doctor → …
  const lastContextLabelRef = useRef(null);
  // Onboarding coach mark: shown once per Premium upgrade epoch. The
  // /usage endpoint returns `last_premium_upgraded_at` (bumped by the
  // Stripe checkout paths whenever a user reaches Premium); if that
  // timestamp is newer than the one we recorded when we marked the
  // tour seen, we replay it so returning Premium users rediscover
  // portfolio-aware capabilities.
  const [tourSeen, setTourSeen] = useState(() => hasSeenAdvisorTour());
  useEffect(() => {
    if (!usage) return;
    setTourSeen(hasSeenAdvisorTour(usage.last_premium_upgraded_at));
  }, [usage]);
  const tourAnalyticsRef = useRef(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const historyLoadedRef = useRef(false);

  // Portfolio-Aware Advisor mounts can supply page-specific suggested
  // questions via the provider; fall back to the default deal prompts.
  const providerPrompts = propertyContext?.__suggestedQuestions;
  const suggestedPrompts = Array.isArray(providerPrompts) && providerPrompts.length > 0
    ? providerPrompts
    : [
        t('aiAdvisor.prompts.dealScore'),
        t('aiAdvisor.prompts.improveCashFlow'),
        t('aiAdvisor.prompts.risks'),
        t('aiAdvisor.prompts.negotiation'),
      ];

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  // Load prior conversation + usage on first open
  useEffect(() => {
    if (!open) return;
    // Usage refreshes every time the panel opens so plan changes / new turns
    // reflect immediately.
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/property/chat/usage`, {
          credentials: 'include',
          headers: { 'Accept-Language': i18n.language || 'fr' },
        });
        if (res.ok) setUsage(await res.json());
      } catch {
        // usage is best-effort; the AI will still reject on the server if needed
      }
    })();

    if (historyLoadedRef.current) return;
    const ctx = propertyContext || {};
    const isPortfolioAware = Boolean(ctx.__contextType);
    // Portfolio-Aware mounts share one thread per user; Deal-Advisor
    // mounts need at least an address to derive a per-deal key.
    if (!isPortfolioAware && !ctx.address) return;
    historyLoadedRef.current = true;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (isPortfolioAware) {
          params.set('property_key', PORTFOLIO_ADVISOR_KEY);
        } else {
          params.set('address', ctx.address);
          if (ctx.purchase_price) params.set('purchase_price', ctx.purchase_price);
        }
        const res = await fetch(`${BACKEND_URL}/api/property/chat/history?${params}`, {
          credentials: 'include',
          headers: { 'Accept-Language': i18n.language || 'fr' },
        });
        if (!res.ok) return;
        const data = await res.json();
        setPropertyKey(data.property_key);
        if (Array.isArray(data.messages) && data.messages.length) {
          setMessages(
            data.messages.map((m) => ({
              id: m.id || `${m.role}-${Math.random()}`,
              role: m.role,
              content: m.content,
            }))
          );
        }
      } catch {
        // silent: history is best-effort
      }
    })();
  }, [open, propertyContext, i18n.language]);

  // Abort any in-flight stream on unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  const handleCopy = useCallback(
    async (id, content) => {
      try {
        await navigator.clipboard.writeText(content);
        setCopiedId(id);
        toast.success(t('common.copied'));
        fireAnalytics('copy_message', propertyKey, { length: content.length });
        setTimeout(() => setCopiedId(null), 1500);
      } catch {
        toast.error(t('errors.serverError'));
      }
    },
    [t, propertyKey]
  );

  const streamFromBackend = useCallback(
    async ({ userText, historySnapshot, assistantId, regenerate = false }) => {
      const controller = new AbortController();
      abortRef.current = controller;
      let firstDelta = true;
      try {
        const res = await fetch(`${BACKEND_URL}/api/property/chat`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'Accept-Language': i18n.language || 'fr',
          },
          body: JSON.stringify({
            message: userText,
            // Portfolio-Aware mounts pin the property_key to the shared
            // thread; Deal-Advisor mounts use whatever the history load
            // returned (per-deal key derived from address+price).
            property_key:
              propertyContext?.__contextType
                ? PORTFOLIO_ADVISOR_KEY
                : (propertyKey || undefined),
            context: propertyContext || {},
            regenerate,
            // Portfolio-Aware Advisor payload — a page like MyPortfolio /
            // PortfolioDoctor / StressTest / RentOptimizer mounts the
            // Advisor via <PortfolioAwareAdvisor contextType="…" context={…}>
            // and the provider stashes both under `__`-prefixed keys. We
            // strip the prefix here so the backend gets a clean dict.
            context_type: propertyContext?.__contextType || undefined,
            page_context: propertyContext?.__pageContext || undefined,
            history: historySnapshot
              .filter((m) => !m.error && (m.role === 'user' || m.role === 'assistant') && m.content)
              .map((m) => ({ role: m.role, content: m.content })),
          }),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let idx;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const line = rawEvent.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            const payload = line.slice(5).trim();
            if (!payload) continue;
            let evt;
            try {
              evt = JSON.parse(payload);
            } catch {
              continue;
            }
            if (evt.type === 'delta' && evt.content) {
              if (firstDelta) {
                firstDelta = false;
                setIsTyping(false);
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: m.content + evt.content, streaming: true }
                    : m
                )
              );
            } else if (evt.type === 'done') {
              if (evt.property_key) setPropertyKey(evt.property_key);
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m))
              );
              // Refresh usage so the header + gate reflect the newly-consumed turn.
              fetch(`${BACKEND_URL}/api/property/chat/usage`, {
                credentials: 'include',
                headers: { 'Accept-Language': i18n.language || 'fr' },
              })
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => d && setUsage(d))
                .catch(() => {});
            } else if (evt.type === 'upgrade_required') {
              setUsage((prev) => ({
                ...(prev || {}),
                plan: evt.plan,
                advisor_access: false,
              }));
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: evt.content, streaming: false, error: true, upgradeRequired: true }
                    : m
                )
              );
            } else if (evt.type === 'quota_exceeded') {
              // Update local usage from the payload the server just sent.
              setUsage((prev) => ({
                ...(prev || {}),
                plan: evt.plan,
                monthly_message_quota: evt.quota,
                used_this_month: evt.used,
                remaining: 0,
                unlimited: false,
              }));
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: evt.content, streaming: false, error: true, quotaExceeded: true }
                    : m
                )
              );
            } else if (evt.type === 'error') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? {
                        ...m,
                        content: evt.content || t('aiAdvisor.errorGeneric'),
                        streaming: false,
                        error: true,
                      }
                    : m
                )
              );
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content: t('aiAdvisor.errorGeneric'),
                  streaming: false,
                  error: true,
                }
              : m
          )
        );
      } finally {
        setIsTyping(false);
      }
    },
    [i18n.language, propertyContext, propertyKey, t]
  );

  const sendMessage = useCallback(
    (text) => {
      const content = text.trim();
      if (!content || isTyping) return;
      // Server-side is source of truth, but block obvious over-quota
      // submissions on the client so we don't waste an HTTP round trip.
      if (
        usage &&
        !usage.unlimited &&
        usage.remaining != null &&
        usage.remaining === 0
      ) {
        if (!proLimitFiredRef.current) {
          proLimitFiredRef.current = true;
          fireAnalytics('ai_advisor_pro_limit_reached', propertyKey, { plan: usage.plan });
        }
        return;
      }
      const userMessage = { id: `u-${Date.now()}`, role: 'user', content };
      const assistantId = `a-${Date.now() + 1}`;
      // Pro users asking portfolio-scoped questions get an "Explore Premium"
      // chip on the reply so the upgrade path is a single tap.
      const isProDealOnly =
        usage && usage.advisor_access === true && usage.portfolio_context === false;
      const portfolioNudge = isProDealOnly && detectPortfolioIntent(content);
      if (portfolioNudge) {
        fireAnalytics('ai_advisor_portfolio_question_blocked', propertyKey, {
          plan: usage?.plan,
        });
      }
      const assistantMessage = {
        id: assistantId, role: 'assistant', content: '', streaming: true,
        portfolioNudge,
        // Pass the user's original text through so the nudge chip can save
        // it for one-tap retry after upgrade — avoids the chip having to
        // walk the messages array to find the immediately-prior user turn.
        parentUserQuestion: portfolioNudge ? content : undefined,
      };
      const historySnapshot = messages;
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setInput('');
      setIsTyping(true);
      fireAnalytics('ai_advisor_question_sent', propertyKey, { plan: usage?.plan });
      streamFromBackend({ userText: content, historySnapshot, assistantId });
    },
    [isTyping, messages, propertyKey, streamFromBackend, usage]
  );

  const handleRegenerate = useCallback(() => {
    if (isTyping) return;
    const reverseIdx = [...messages].reverse().findIndex((m) => m.role === 'assistant');
    if (reverseIdx === -1) return;
    const assistantAbsIdx = messages.length - 1 - reverseIdx;
    const userTurn = messages
      .slice(0, assistantAbsIdx)
      .reverse()
      .find((m) => m.role === 'user');
    if (!userTurn) return;

    fireAnalytics('regenerate_clicked', propertyKey);

    const assistantId = `a-${Date.now()}`;
    setMessages((prev) => {
      const next = prev.slice(0, assistantAbsIdx);
      next.push({ id: assistantId, role: 'assistant', content: '', streaming: true });
      return next;
    });
    setIsTyping(true);
    const priorHistory = messages.slice(
      0,
      messages.findIndex((m) => m.id === userTurn.id)
    );
    streamFromBackend({
      userText: userTurn.content,
      historySnapshot: priorHistory,
      assistantId,
      regenerate: true,
    });
  }, [isTyping, messages, propertyKey, streamFromBackend]);

  const handleRetry = useCallback(
    (errorMsgId) => {
      if (isTyping) return;
      const errIdx = messages.findIndex((m) => m.id === errorMsgId);
      if (errIdx === -1) return;
      const userTurn = messages.slice(0, errIdx).reverse().find((m) => m.role === 'user');
      if (!userTurn) return;

      fireAnalytics('retry_clicked', propertyKey);

      const assistantId = `a-${Date.now()}`;
      setMessages((prev) => {
        const next = prev.slice(0, errIdx);
        next.push({ id: assistantId, role: 'assistant', content: '', streaming: true });
        return next;
      });
      setIsTyping(true);
      const priorHistory = messages.slice(
        0,
        messages.findIndex((m) => m.id === userTurn.id)
      );
      streamFromBackend({
        userText: userTurn.content,
        historySnapshot: priorHistory,
        assistantId,
      });
    },
    [isTyping, messages, propertyKey, streamFromBackend]
  );

  const handleClear = async () => {
    abortRef.current?.abort();
    setMessages([]);
    setIsTyping(false);
    toast.success(t('aiAdvisor.cleared'));
    const ctx = propertyContext || {};
    const isPortfolioAware = Boolean(ctx.__contextType);
    if (!isPortfolioAware && !ctx.address && !propertyKey) return;
    try {
      const params = new URLSearchParams();
      // Portfolio-Aware threads live under the shared key; deal-advisor
      // threads live under the per-deal key computed by the backend.
      if (isPortfolioAware) {
        params.set('property_key', PORTFOLIO_ADVISOR_KEY);
      } else {
        if (propertyKey) params.set('property_key', propertyKey);
        if (ctx.address) params.set('address', ctx.address);
        if (ctx.purchase_price) params.set('purchase_price', ctx.purchase_price);
      }
      await fetch(`${BACKEND_URL}/api/property/chat/history?${params}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Accept-Language': i18n.language || 'fr' },
      });
    } catch {
      // best effort
    }
  };

  const handleStartNewConversation = () => {
    fireAnalytics('advisor_thread_cleared', propertyKey, {
      plan: usage?.plan,
      length: messages.length,
      trigger: 'long_thread_pill',
    });
    handleClear();
  };

  const handleOpen = () => {
    setOpen(true);
    fireAnalytics('panel_opened', propertyKey, { language: i18n.language });
    fireAnalytics('ai_advisor_opened', propertyKey, { language: i18n.language, plan: usage?.plan });
  };
  const handleClose = () => {
    setOpen(false);
    fireAnalytics('panel_closed', propertyKey);
  };
  const handleSuggested = (prompt, idx) => {
    fireAnalytics('suggested_prompt_clicked', propertyKey, { idx });
    sendMessage(prompt);
  };

  // id of the last non-error, non-streaming assistant bubble (Regenerate target).
  let lastAssistantId = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.role === 'assistant' && !m.error && !m.streaming) {
      lastAssistantId = m.id;
      break;
    }
  }

  const footer = (
    <ChatInput
      ref={inputRef}
      value={input}
      onChange={setInput}
      onSubmit={() => sendMessage(input)}
      disabled={isTyping}
      placeholder={t('aiAdvisor.inputPlaceholder')}
      disclaimer={t('aiAdvisor.disclaimer')}
      sendLabel={t('aiAdvisor.send')}
    />
  );

  // ---- Usage pill: only shown to metered plans ----
  const usageBadge = usage && !usage.unlimited && usage.monthly_message_quota != null ? (
    <span
      data-testid="ai-chat-usage-badge"
      title={t('aiAdvisor.usageTooltip', {
        used: usage.used_this_month,
        quota: usage.monthly_message_quota,
        plan: (usage.plan || '').toUpperCase(),
      })}
      className={`text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap mr-1 ${
        usage.remaining === 0
          ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300'
          : usage.remaining != null && usage.remaining <= 10
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
      }`}
    >
      {usage.remaining != null ? usage.remaining : usage.used_this_month}/{usage.monthly_message_quota}
    </span>
  ) : null;

  // ---- Premium badge (unlimited advisor) ----
  const premiumBadge = usage?.portfolio_context ? (
    <span
      data-testid="ai-chat-premium-badge"
      className="text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 whitespace-nowrap mr-1 inline-flex items-center gap-1"
    >
      <Sparkles className="w-3 h-3" aria-hidden="true" />
      {t('aiAdvisor.tier.premiumBadge')}
    </span>
  ) : null;

  // ---- Personalized Investor Fit chip (deal-scoped) ----
  // Refreshes when the panel opens so the chip stays in sync after profile edits.
  const fitBadge = propertyContext?.deal_id ? (
    <FitChip dealId={propertyContext.deal_id} refreshKey={open ? 'open' : 'closed'} />
  ) : null;

  const headerBadges = (fitBadge || premiumBadge || usageBadge) ? (
    <div className="flex items-center gap-1">
      {fitBadge}
      {premiumBadge}
      {usageBadge}
    </div>
  ) : null;

  // ---- Tier-aware title & subtitle ----
  // Portfolio-Aware Advisor mounts (MyPortfolio / PortfolioDoctor /
  // StressTest / RentOptimizer) inject a context label like
  // "AI Advisor · My Portfolio" so the user always knows which view
  // grounds the answer. When a contextLabel is present we drop the
  // long tier title so the compact "AI Advisor · <label>" fits in the
  // ~420px panel header without truncation.
  const pageContextLabel = propertyContext?.__contextLabel;
  const baseTierTitle = usage?.portfolio_context
    ? t('aiAdvisor.tier.premiumTitle')
    : usage?.advisor_access
      ? t('aiAdvisor.tier.proTitle')
      : t('aiAdvisor.title');
  const tierTitle = pageContextLabel && usage?.portfolio_context
    ? `${t('aiAdvisor.tier.contextTitle', { defaultValue: 'AI Advisor' })} · ${pageContextLabel}`
    : baseTierTitle;
  const tierSubtitle = usage?.portfolio_context
    ? t('aiAdvisor.tier.premiumSubtitle')
    : usage?.advisor_access
      ? t('aiAdvisor.tier.proSubtitle')
      : t('aiAdvisor.subtitle');

  // ---- Fire warning + limit analytics side-effects ----
  useEffect(() => {
    if (!usage || usage.unlimited || usage.remaining == null) return;
    if (usage.remaining > 0 && usage.remaining <= 10 && !proWarningFiredRef.current) {
      proWarningFiredRef.current = true;
      fireAnalytics('ai_advisor_pro_limit_warning', propertyKey, {
        remaining: usage.remaining, plan: usage.plan,
      });
    }
    if (usage.remaining === 0 && !proLimitFiredRef.current) {
      proLimitFiredRef.current = true;
      fireAnalytics('ai_advisor_pro_limit_reached', propertyKey, { plan: usage.plan });
    }
  }, [usage, propertyKey]);

  // ---- Cross-Page Conversation: context-change notice ----
  // When the Advisor thread persists across Premium pages, insert a
  // subtle inline system row so the user always knows which view is
  // now grounding answers. Only fires when a REAL context switch
  // happens (label transitions to a different non-null value) and
  // there is at least one existing message so the empty state stays
  // pristine.
  useEffect(() => {
    if (!pageContextLabel) {
      lastContextLabelRef.current = null;
      return;
    }
    const prev = lastContextLabelRef.current;
    lastContextLabelRef.current = pageContextLabel;
    if (!prev || prev === pageContextLabel) return;
    if (messages.length === 0) return;
    setMessages((cur) => [
      ...cur,
      {
        id: `ctx-${Date.now()}`,
        role: 'system',
        kind: 'context_change',
        content: t('aiAdvisor.contexts.changedNotice', { context: pageContextLabel }),
      },
    ]);
    fireAnalytics('advisor_context_changed', propertyKey, {
      to: propertyContext?.__contextType,
      label: pageContextLabel,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageContextLabel]);

  // ---- One-tap retry after upgrade: replay pending portfolio question ----
  // Runs once when the panel is open and the user is now Premium AND has a
  // fresh pending question from a portfolio nudge click. Prompts a toast
  // and dispatches sendMessage() so the answer streams in immediately.
  useEffect(() => {
    if (autoRetriedRef.current) return;
    if (!open) return;
    if (!usage || !usage.portfolio_context) return;
    if (isTyping) return;
    if (messages.length > 0) return; // Only replay into a fresh conversation
    const pending = readPendingQuestion();
    if (!pending || !pending.question) return;
    autoRetriedRef.current = true;
    clearPendingQuestion();
    toast.success(t('aiAdvisor.portfolioNudge.autoRetryToast'));
    fireAnalytics('ai_advisor_premium_portfolio_query', propertyKey, {
      source: 'auto_retry_after_upgrade',
    });
    // Slight delay so the toast renders before the streaming starts.
    setTimeout(() => sendMessage(pending.question), 250);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, usage, isTyping, messages.length]);

  const goToPricing = (target) => {
    fireAnalytics('ai_advisor_upgrade_clicked', propertyKey, {
      target, plan: usage?.plan,
    });
    navigate('/pricing');
  };

  // ---- FREE upgrade state ----
  // Server-side is source of truth (403 on /property/chat); the frontend
  // just swaps the panel body so Free users see a clean, on-brand upsell.
  const isFree = usage && usage.advisor_access === false;

  // ---- Chat body: gated / limit-reached / normal ----
  let panelBody;
  if (isFree) {
    panelBody = (
      <div
        data-testid="ai-advisor-free-upgrade"
        className="flex flex-col items-center justify-center text-center px-6 py-10 h-full gap-4"
      >
        <div className="w-14 h-14 rounded-2xl bg-[#12355B]/10 dark:bg-[#45C04A]/10 flex items-center justify-center">
          <Lock className="w-7 h-7 text-[#12355B] dark:text-[#45C04A]" aria-hidden="true" />
        </div>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {t('aiAdvisor.upgrade.freeTitle')}
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xs">
          {t('aiAdvisor.upgrade.freeBody')}
        </p>
        <button
          data-testid="ai-advisor-upgrade-pro-cta"
          onClick={() => goToPricing('pro')}
          className="rp-advisor-focus inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#12355B] hover:bg-[#12355B]/90 text-white text-sm font-medium transition-colors"
        >
          {t('aiAdvisor.upgrade.cta')}
          <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    );
  } else if (usage && !usage.unlimited && usage.remaining === 0) {
    panelBody = (
      <div
        data-testid="ai-advisor-limit-reached"
        className="flex flex-col items-center justify-center text-center px-6 py-10 h-full gap-4"
      >
        <div className="w-14 h-14 rounded-2xl bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center">
          <Lock className="w-7 h-7 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        </div>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {t('aiAdvisor.quota.limitReachedTitle')}
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 max-w-sm">
          {t('aiAdvisor.quota.limitReachedBody', { quota: usage.monthly_message_quota })}
        </p>
        <button
          data-testid="ai-advisor-upgrade-premium-cta"
          onClick={() => goToPricing('premium')}
          className="rp-advisor-focus inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
        >
          {t('aiAdvisor.quota.upgradeToPremium')}
          <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    );
  } else if (messages.length === 0) {
    const showTour =
      !tourSeen && usage?.portfolio_context === true;
    if (showTour && !tourAnalyticsRef.current) {
      tourAnalyticsRef.current = true;
      // Fire outside render on the next tick to avoid double-log in Strict Mode
      setTimeout(() => fireAnalytics('advisor_tour_shown', propertyKey, {
        plan: usage?.plan,
      }), 0);
    }
    panelBody = showTour ? (
      <AdvisorTourCard
        t={t}
        onDismiss={() => {
          markAdvisorTourSeen(usage?.last_premium_upgraded_at);
          setTourSeen(true);
          fireAnalytics('advisor_tour_dismissed', propertyKey, {
            plan: usage?.plan,
            epoch: usage?.last_premium_upgraded_at || null,
          });
        }}
      />
    ) : (
      <SuggestedPrompts
        prompts={suggestedPrompts}
        onSelect={handleSuggested}
        title={t('aiAdvisor.emptyTitle')}
        subtitle={t('aiAdvisor.emptySubtitle')}
        header={t('aiAdvisor.suggested')}
      />
    );
  } else {
    panelBody = (
      <>
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            propertyKey={propertyKey}
            copied={copiedId === msg.id}
            isLastAssistant={msg.id === lastAssistantId}
            onCopy={handleCopy}
            onRegenerate={handleRegenerate}
            onRetry={() => handleRetry(msg.id)}
            onFollowup={sendMessage}
          />
        ))}
        {isTyping && <TypingIndicator />}
      </>
    );
  }

  // ---- Low-usage warning banner (Pro, remaining ≤ 10, non-zero) ----
  const showLowWarning =
    usage && !usage.unlimited && usage.remaining != null &&
    usage.remaining > 0 && usage.remaining <= 10 && !isFree && messages.length > 0;

  const warningBanner = showLowWarning ? (
    <div
      data-testid="ai-advisor-low-usage-warning"
      className="mx-3 mb-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-xs text-amber-900 dark:text-amber-200 flex items-center justify-between gap-2"
    >
      <span>{t('aiAdvisor.quota.warning', { count: usage.remaining })}</span>
      <button
        data-testid="ai-advisor-low-usage-upgrade"
        onClick={() => goToPricing('premium')}
        className="rp-advisor-focus font-semibold underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-100 whitespace-nowrap"
      >
        {t('aiAdvisor.quota.upgradeCta')}
      </button>
    </div>
  ) : null;

  // ---- Long-thread "Start a new conversation" pill ----
  // Premium-only, portfolio-aware-only. Once the shared thread reaches
  // 10 messages we surface an obvious pill so users can start fresh
  // without hunting for the clear icon. Deal-advisor threads keep the
  // existing header X button.
  const showLongThreadPill =
    usage?.portfolio_context === true &&
    Boolean(propertyContext?.__contextType) &&
    messages.length >= LONG_THREAD_THRESHOLD;

  const longThreadPill = showLongThreadPill ? (
    <button
      data-testid="ai-advisor-start-new-conversation"
      onClick={handleStartNewConversation}
      className="rp-advisor-focus mx-3 mt-2 mb-1 self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold transition-colors"
      aria-label={t('aiAdvisor.thread.startNewAria')}
    >
      <Sparkles className="w-3 h-3" aria-hidden="true" />
      {t('aiAdvisor.thread.startNew')}
    </button>
  ) : null;

  return (
    <>
      {!open && (
        <ChatFloatingButton
          label={t('aiAdvisor.buttonLabel')}
          ariaLabel={t('aiAdvisor.openButton')}
          onClick={handleOpen}
        />
      )}

      <ChatPanel
        open={open}
        onClose={handleClose}
        onClear={handleClear}
        canClear={messages.length > 0}
        title={tierTitle}
        subtitle={tierSubtitle}
        clearLabel={t('aiAdvisor.clear')}
        headerBadge={headerBadges}
        scrollRef={scrollRef}
        footer={isFree || (usage && !usage.unlimited && usage.remaining === 0) ? null : (
          <>
            {warningBanner}
            {footer}
          </>
        )}
      >
        {longThreadPill}
        {panelBody}
      </ChatPanel>
    </>
  );
}

// ============= AdvisorTourCard =============
// One-time coach mark introducing the Portfolio-Aware Advisor. Rendered
// in place of the SuggestedPrompts empty state for Premium users whose
// `rp_advisor_tour_seen_v1` flag is not yet set. Small (fits in 420px
// panel), on-brand, dismissible, keyboard-accessible.
function AdvisorTourCard({ t, onDismiss }) {
  const items = [
    { key: 'portfolio', Icon: Building2 },
    { key: 'doctor', Icon: HeartPulse },
    { key: 'stress', Icon: Zap },
    { key: 'rent', Icon: Target },
  ];
  return (
    <div
      data-testid="advisor-tour-card"
      className="rp-advisor-msg-in flex flex-col gap-4 px-1 py-2"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h3 className="font-primary font-bold text-base text-slate-900 dark:text-slate-100">
            {t('aiAdvisor.tour.title')}
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
            {t('aiAdvisor.tour.subtitle')}
          </p>
        </div>
      </div>
      <ul className="flex flex-col gap-2.5">
        {items.map(({ key, Icon }) => (
          <li key={key} className="flex items-start gap-2.5 text-xs">
            <Icon
              className="w-4 h-4 text-[#12355B] dark:text-[#45C04A] shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <span className="text-slate-700 dark:text-slate-300 leading-snug">
              <strong className="font-semibold text-slate-900 dark:text-slate-100">
                {t(`aiAdvisor.tour.items.${key}.title`)}
              </strong>
              {' — '}
              {t(`aiAdvisor.tour.items.${key}.body`)}
            </span>
          </li>
        ))}
      </ul>
      <button
        data-testid="advisor-tour-dismiss"
        onClick={onDismiss}
        className="rp-advisor-focus mt-1 self-start inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white text-xs font-semibold transition-colors"
      >
        {t('aiAdvisor.tour.cta')}
      </button>
    </div>
  );
}
